/**
 * Internal-linking helper for the Insights hub.
 *
 * Each category maps to a short list of relevant on-site destinations,
 * keeping articles wired into the rest of the website (Home anchors,
 * Franchise chapter, and back to the relevant Insights category index).
 *
 * Anchors here all point to existing single-page sections in Home.jsx
 * (#systems, #franchise, #proof, #sectors, #enquiry). Keeping the data
 * in one place so it stays consistent across articles.
 */
export const SERVICE_LINKS_BY_CATEGORY = {
  "AI Visibility": [
    {
      to: "/#systems",
      label: "Digital Discoverability",
      blurb: "Premium presence across search & AI assistants.",
    },
    {
      to: "/#systems",
      label: "Premium Websites & Digital Presence",
      blurb: "Considered websites that read clearly to AI.",
    },
    {
      to: "/insights",
      label: "More on AI Visibility",
      blurb: "Browse related insights.",
      external: false,
      isCategory: true,
      category: "AI Visibility",
    },
  ],
  "Business Automation": [
    {
      to: "/#systems",
      label: "Automation & Workflow Infrastructure",
      blurb: "Quiet automation across your operation.",
    },
    {
      to: "/#franchise",
      label: "Multi-Location Automation Workflows",
      blurb: "Scalable automation for multi-site brands.",
    },
    {
      to: "/insights",
      label: "More on Business Automation",
      blurb: "Browse related insights.",
      isCategory: true,
      category: "Business Automation",
    },
  ],
  "CRM Systems": [
    {
      to: "/#systems",
      label: "CRM & Customer Journey Systems",
      blurb: "A single source of truth for your customer journey.",
    },
    {
      to: "/#franchise",
      label: "Franchise CRM Systems",
      blurb: "Multi-location CRM for scalable brands.",
    },
    {
      to: "/insights",
      label: "More on CRM Systems",
      blurb: "Browse related insights.",
      isCategory: true,
      category: "CRM Systems",
    },
  ],
  Websites: [
    {
      to: "/#systems",
      label: "Premium Websites & Digital Presence",
      blurb: "Calm, credible, conversion-aware websites.",
    },
    {
      to: "/#proof",
      label: "Featured Projects",
      blurb: "See selected digital ecosystems we have built.",
    },
    {
      to: "/insights",
      label: "More on Websites",
      blurb: "Browse related insights.",
      isCategory: true,
      category: "Websites",
    },
  ],
  "Lead Generation": [
    {
      to: "/#systems",
      label: "CRM & Customer Journey Systems",
      blurb: "Capture, route, and follow up with intent.",
    },
    {
      to: "/#franchise",
      label: "Multi-Location Lead Management",
      blurb: "Lead routing across locations & territories.",
    },
    {
      to: "/insights",
      label: "More on Lead Generation",
      blurb: "Browse related insights.",
      isCategory: true,
      category: "Lead Generation",
    },
  ],
  "Customer Experience": [
    {
      to: "/#systems",
      label: "Bespoke Apps & Client Portals",
      blurb: "Considered portals for premium relationships.",
    },
    {
      to: "/#sectors",
      label: "Sectors we work with",
      blurb: "Hospitality, travel, premium services.",
    },
    {
      to: "/insights",
      label: "More on Customer Experience",
      blurb: "Browse related insights.",
      isCategory: true,
      category: "Customer Experience",
    },
  ],
  "Case Studies": [
    {
      to: "/#proof",
      label: "Featured Projects",
      blurb: "GuestVoyages, Southern Cross Blue Cruising and more.",
    },
    {
      to: "/#franchise",
      label: "Franchise Infrastructure",
      blurb: "Quiet systems behind scalable, multi-location brands.",
    },
    {
      to: "/insights",
      label: "More case studies",
      blurb: "Browse related insights.",
      isCategory: true,
      category: "Case Studies",
    },
  ],
};

/**
 * Build a stable canonical URL for client-side routes.
 */
export function buildCanonical(pathname) {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://linked2marketing.com";
  return `${origin}${pathname}`;
}
